import type {
    PackageNode,
    PackageNodeItem,
    PackageNodeItemOption,
    PackageQuestion,
    TemplatePackage,
} from '@/features/survey/engine';

import type { ApiPackageEdge, ApiPackageItem, ApiTemplatePackage } from './types';

/**
 * API-pakket → enginepakket
 * =========================
 *
 * De engine is geschreven tegen een genest `TemplatePackage` (knoop → items →
 * randen) en rekent geverifieerd hetzelfde als de API. Het echte pakket van
 * `GET /template-versions/{uuid}/package` is plat. Deze adapter vertaalt; de
 * engine blijft ongemoeid.
 *
 * Verschillen, en hoe ze vertaald worden:
 *
 * | API-pakket                              | enginepakket                                   |
 * | --------------------------------------- | ---------------------------------------------- |
 * | `version {uuid, version, template {…}}` | `version`, `name`, `inspection_type` (code)    |
 * | geen numerieke versie- of template-id   | `template_version_id` / `template_id` = `0`; de engine leest ze niet, de uuid staat in `TemplateVersionRecord` |
 * | `nodes[]`, `items[]`, `edges[]` plat    | `nodes[].items[].options[]` genest via `node_id` en `item_id` |
 * | `nodes[].in_report`                     | `in_report_by_default`                         |
 * | `nodes[].path/depth/section_path/…`     | weggelaten: de engine leidt de nummering af, want multi-instanties verschuiven hem |
 * | `edges[].question_option_id = null`     | `-edge.id`: een keuzevraag kan hem nooit selecteren, een waardevraag volgt hem wél - exact de regel van de PHP-loader |
 * | `edges[].cost_multiplier_source`        | weggelaten: de engine vermenigvuldigt op het vraagtype (`number`); in NTA 8060 valt dat 482 op 482 samen |
 * | `questions[].options[].is_clean`        | weggelaten: de sweep kiest zelf op label en rand (`sweepAnswerFor`) |
 * | `cost_groups[].legacy_id`, `edges[].question_option_legacy_id`, `items[].node_id` | weggelaten |
 *
 * Een item waarvan de knoop niet in het pakket staat, of een rand waarvan het
 * item ontbreekt, valt weg: die zijn in de engine onbereikbaar.
 */
export function toEnginePackage(document: ApiTemplatePackage): TemplatePackage {
    const edgesByItem = groupBy(document.edges, (edge) => edge.item_id);
    const itemsByNode = groupBy(document.items, (item) => item.node_id);

    const questions: PackageQuestion[] = document.questions.map((question) => ({
        id: question.id,
        legacy_id: question.legacy_id,
        type: question.type,
        label: question.label,
        help_text: question.help_text,
        photo_requirement: question.photo_requirement,
        photo_min: question.photo_min,
        number_min: question.number_min,
        number_max: question.number_max,
        number_unit: question.number_unit,
        number_decimals: question.number_decimals,
        special: question.special,
        ask_once: question.ask_once,
        sort_order: question.sort_order,
        options: question.options.map((option) => ({
            id: option.id,
            legacy_id: option.legacy_id,
            label: option.label,
            sort_order: option.sort_order,
            is_exclusive: option.is_exclusive,
        })),
    }));

    const nodes: PackageNode[] = document.nodes.map((node) => ({
        id: node.id,
        parent_id: node.parent_id,
        legacy_id: node.legacy_id,
        name: node.name,
        sort_order: node.sort_order,
        nhg_code: node.nhg_code,
        is_multi: node.is_multi,
        multi_question_label: node.multi_question_label,
        multi_instance_label: node.multi_instance_label,
        multi_report_text: node.multi_report_text,
        in_report_by_default: node.in_report,
        items: (itemsByNode.get(node.id) ?? []).map((item) =>
            toEngineItem(item, edgesByItem.get(item.id) ?? []),
        ),
    }));

    return {
        template_version_id: 0,
        template_id: 0,
        version: document.version.version,
        name: document.version.template.name,
        inspection_type: document.version.template.inspection_type.code,
        checksum: document.checksum,
        generated_at: document.generated_at,
        cost_groups: document.cost_groups.map((group) => ({
            id: group.id,
            code: group.code,
            name: group.name,
            long_name: group.long_name,
            sort_order: group.sort_order,
        })),
        questions,
        nodes,
    };
}

function toEngineItem(item: ApiPackageItem, edges: readonly ApiPackageEdge[]): PackageNodeItem {
    return {
        id: item.id,
        legacy_id: item.legacy_id,
        question_id: item.question_id,
        is_start: item.is_start,
        sort_order: item.sort_order,
        options: edges.map(toEngineOption),
    };
}

/** Een wees-rand krijgt een negatief, gegarandeerd onbestaand optie-id (zoals de PHP-loader). */
export function orphanOptionId(edgeId: number): number {
    return -edgeId;
}

function toEngineOption(edge: ApiPackageEdge): PackageNodeItemOption {
    return {
        id: edge.id,
        question_option_id: edge.question_option_id ?? orphanOptionId(edge.id),
        result: edge.result,
        next_item_id: edge.next_item_id,
        report_text: edge.report_text,
        direct_cost: edge.direct_cost,
        term_cost: edge.term_cost,
        improvement_cost: edge.improvement_cost,
        cost_group_id: edge.cost_group_id,
        is_clean: edge.is_clean,
        sort_order: edge.sort_order,
        amount_prompt: edge.amount_prompt,
    };
}

function groupBy<T, K>(rows: readonly T[], keyOf: (row: T) => K): Map<K, T[]> {
    const groups = new Map<K, T[]>();

    for (const row of rows) {
        const key = keyOf(row);
        const group = groups.get(key);

        if (group) {
            group.push(row);
        } else {
            groups.set(key, [row]);
        }
    }

    return groups;
}
